import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { AssociarProdutoItensAvulsosRequest } from '../../requests/compras/associar-produto-itens-avulsos.request';
import { AtualizarCompraRequest } from '../../requests/compras/atualizar-compra.request';
import { AtualizarTituloCompraRequest } from '../../requests/compras/atualizar-titulo-compra.request';
import { CriarCompraOcorrenciaRequest } from '../../requests/compras/criar-compra-ocorrencia.request';
import { SolicitacaoCompraRequest } from '../../requests/compras/solicitacao-compra.request';
import { CompraItemAvulsoResponse } from '../../responses/compras/compra-item-avulso.response';
import { CriarSolicitacaoCompraResponse } from '../../responses/compras/criar-solicitacao-compra.response';
import { DetalheCompraResponse } from '../../responses/compras/detalhe-compra.response';
import { ListarComprasKanbanResponse } from '../../responses/compras/listar-compras-kanban.response';
import { ListarComprasKanbanPaginadoResponse } from '../../responses/compras/listar-compras-kanban-paginado.response';
import { ListarComprasEtapaPaginadoResponse } from '../../responses/compras/listar-compras-etapa-paginado.response';
import { ListarComprasPaginadoResponse } from '../../responses/compras/listar-compras-paginado.response';
import { GetProdutoComprasPrevistasResponse } from '../../responses/compras/get-produto-compras-previstas.response';
import { AtualizarCompraResponse } from '../../responses/compras/atualizar-compra.response';
import { CompraSplitResponse } from '../../responses/compras/compra-split.response';
import { GetCompraHistoricoResponse } from '../../responses/compras/get-compra-historico.response';
import { GetComprasDoFornecedorResponse } from '@shared/services/responses/compras/get-compras-do-fornecedor.response';
import { ReturnStruct } from '../../structs/return.struct';

export interface ComprasFiltroParams {
  pesquisa?: string | null;
  idUsuarioResponsavel?: number | null;
  idTipoPrioridade?: number | null;
  status?: string | null;
  chaveEtapa?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CompraService extends CommonService {
  private readonly apiUrl: string = environment.urlApiCompras + 'Compra';

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * As compras EM ANDAMENTO que contem um produto — aba "compras previstas" do modal
   * "Detalhes do estoque" do documento comercial (§6.3).
   *
   * Rota propria (`ProdutoCompra`), e nao `Compra`: as rotas de `Compra` exigem
   * `Suprimentos_ProcessoCompras_Visualizar`, que o vendedor nao tem. Esta devolve so
   * quantidade, estagio e previsao — nada da negociacao de compra.
   */
  public getComprasPrevistasDoProduto(idProduto: number, quantidade: number = 10): Observable<GetProdutoComprasPrevistasResponse> {
    return this.httpClient
      .get<GetProdutoComprasPrevistasResponse>(
        `${environment.urlApiCompras}ProdutoCompra/idProduto/${idProduto}/previstas?quantidade=${quantidade}`,
        this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public getDetalhe(idCompra: number): Observable<DetalheCompraResponse> {
    return this.httpClient
      .get<DetalheCompraResponse>(`${this.apiUrl}/${idCompra}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Histórico da compra: ocorrências por data do fato desc (comentário não entra no histórico). */
  public getHistorico(idCompra: number): Observable<GetCompraHistoricoResponse> {
    return this.httpClient
      .get<GetCompraHistoricoResponse>(`${this.apiUrl}/idCompra/${idCompra}/historico`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Últimas compras do fornecedor, com o nome e a cor da etapa (consulta do cadastro de Pessoa). */
  public getUltimasDoFornecedor(idPessoaFornecedor: number, quantidade: number = 10): Observable<GetComprasDoFornecedorResponse> {
    return this.httpClient
      .get<GetComprasDoFornecedorResponse>(
        `${this.apiUrl}/fornecedor/idPessoa/${idPessoaFornecedor}/ultimas?quantidade=${quantidade}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Anotação do usuário no histórico (ocorrência tipo 5). */
  public criarOcorrencia(idCompra: number, request: CriarCompraOcorrenciaRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/idCompra/${idCompra}/ocorrencia`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Item avulso que pré-preenche o cadastro de produto aberto a partir da compra. */
  public getItemAvulso(idCompraItem: number): Observable<CompraItemAvulsoResponse> {
    return this.httpClient
      .get<CompraItemAvulsoResponse>(`${this.apiUrl}/item/${idCompraItem}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public getAll(): Observable<ListarComprasKanbanResponse> {
    return this.httpClient
      .get<ListarComprasKanbanResponse>(this.apiUrl, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public criarSolicitacao(request: SolicitacaoCompraRequest): Observable<CriarSolicitacaoCompraResponse> {
    return this.httpClient
      .post<CriarSolicitacaoCompraResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public atualizar(idCompra: number, request: AtualizarCompraRequest): Observable<AtualizarCompraResponse> {
    return this.httpClient
      .put<AtualizarCompraResponse>(`${this.apiUrl}/${idCompra}`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public atualizarTitulo(idCompra: number, request: AtualizarTituloCompraRequest): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(`${this.apiUrl}/idCompra/${idCompra}/titulo`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Vincula os itens avulsos a produtos do cadastro e remove os avulsos descartados, em lote. */
  public associarProdutoItensAvulsos(idCompra: number, request: AssociarProdutoItensAvulsosRequest): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(`${this.apiUrl}/${idCompra}/itens-avulsos/produto`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Split de pedido da compra: pai (quando ela é filha) ou filhas (quando ela é pai). */
  public getSplit(idCompra: number): Observable<CompraSplitResponse> {
    return this.httpClient
      .get<CompraSplitResponse>(`${this.apiUrl}/${idCompra}/split`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Reverte o split: o saldo volta ao pedido pai e a filha é arquivada. */
  public reverterSplit(idCompraFilho: number): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/${idCompraFilho}/reverterSplit`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Aceita as divergências do vínculo da NF: ajusta preço/saldo do pedido ao da NF-e (não move a etapa). */
  public aceitarDivergenciaVinculo(idCompra: number): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/idCompra/${idCompra}/divergenciaVinculo/aceitar`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public getKanban(filtros: ComprasFiltroParams, tamanhoPagina: number = 10): Observable<ListarComprasKanbanPaginadoResponse> {
    let params = this.montarParamsFiltro(filtros, false);
    params = params.set('tamanhoPagina', tamanhoPagina.toString());

    return this.httpClient
      .get<ListarComprasKanbanPaginadoResponse>(`${this.apiUrl}/kanban`, {
        params,
        ...this.addHeaderToken(),
      })
      .pipe(catchError(this.handleError));
  }

  public getKanbanEtapa(
    chaveEtapa: string,
    filtros: ComprasFiltroParams,
    pagina: number = 0,
    tamanhoPagina: number = 10
  ): Observable<ListarComprasEtapaPaginadoResponse> {
    let params = this.montarParamsFiltro(filtros, false);
    params = params
      .set('chaveEtapa', chaveEtapa)
      .set('pagina', pagina.toString())
      .set('tamanhoPagina', tamanhoPagina.toString());

    return this.httpClient
      .get<ListarComprasEtapaPaginadoResponse>(`${this.apiUrl}/kanban/etapa`, {
        params,
        ...this.addHeaderToken(),
      })
      .pipe(catchError(this.handleError));
  }

  public getLista(filtros: ComprasFiltroParams, pagina: number = 0, tamanhoPagina: number = 20): Observable<ListarComprasPaginadoResponse> {
    let params = this.montarParamsFiltro(filtros, true);
    params = params.set('pagina', pagina.toString()).set('tamanhoPagina', tamanhoPagina.toString());

    return this.httpClient
      .get<ListarComprasPaginadoResponse>(`${this.apiUrl}/lista`, {
        params,
        ...this.addHeaderToken(),
      })
      .pipe(catchError(this.handleError));
  }

  private montarParamsFiltro(filtros: ComprasFiltroParams, incluirChaveEtapa: boolean): HttpParams {
    let params = new HttpParams();

    if (filtros?.pesquisa) {
      params = params.set('pesquisa', filtros.pesquisa);
    }
    if (filtros?.idUsuarioResponsavel !== null && filtros?.idUsuarioResponsavel !== undefined) {
      params = params.set('idUsuarioResponsavel', filtros.idUsuarioResponsavel.toString());
    }
    if (filtros?.idTipoPrioridade !== null && filtros?.idTipoPrioridade !== undefined) {
      params = params.set('idTipoPrioridade', filtros.idTipoPrioridade.toString());
    }
    if (filtros?.status) {
      params = params.set('status', filtros.status);
    }
    if (incluirChaveEtapa && filtros?.chaveEtapa) {
      params = params.set('chaveEtapa', filtros.chaveEtapa);
    }

    return params;
  }
}
